import { Component } from '@theme/component';

/**
 * <orivon-announcement-component>: the ORIVON announcement bar (design §9.1, plan §2.3).
 * - Below 1024px one message shows at a time; every `data-interval` seconds (default 5) the next one fades in.
 *   From 1024px the messages sit inline (CSS) and nothing rotates.
 * - The pause button (`ref="toggle"`, aria-pressed) stops and restarts the rotation. With reduced motion the bar
 *   starts paused. It never rotates while the pointer or keyboard focus is inside, or while the tab is hidden.
 * - Messages are not aria-live: a changing message is never announced.
 * - Theme editor: selecting a message block shows it and holds it until the block is deselected.
 * Loaded by sections/orivon-announcement.liquid only when two or more messages rotate.
 *
 * @typedef {object} AnnouncementRefs
 * @property {HTMLElement[]} [messages] - The rotating messages (`ref="messages[]"`).
 * @property {HTMLButtonElement} [toggle] - The pause / play button.
 *
 * @extends {Component<AnnouncementRefs>}
 */
export class OrivonAnnouncementComponent extends Component {
  #index = 0;
  /** @type {number | undefined} */
  #timer = undefined;
  #paused = false;
  #held = false;
  /** @type {AbortController | null} */
  #abortController = null;
  #desktop = window.matchMedia('(min-width: 64em)');

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const messages = this.refs.messages ?? [];
    const active = messages.findIndex((message) => message.classList.contains('is-active'));
    this.#index = active > -1 ? active : 0;
    this.#show(this.#index);

    this.#paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#syncToggle();

    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });

    this.#timer = window.setInterval(this.#tick, this.#interval);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    window.clearInterval(this.#timer);
  }

  /** Pauses or resumes the rotation (pause button). */
  toggle() {
    this.#paused = !this.#paused;
    this.#syncToggle();
  }

  get #interval() {
    const seconds = parseInt(this.dataset.interval ?? '', 10);
    return (Number.isNaN(seconds) ? 5 : Math.min(Math.max(seconds, 3), 10)) * 1000;
  }

  #tick = () => {
    if (this.#paused || this.#held || this.#desktop.matches || document.hidden) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;

    const count = this.refs.messages?.length ?? 0;
    if (count < 2) return;

    this.#show((this.#index + 1) % count);
  };

  /** @param {number} index */
  #show(index) {
    this.#index = index;
    (this.refs.messages ?? []).forEach((message, i) => message.classList.toggle('is-active', i === index));
  }

  #syncToggle() {
    this.refs.toggle?.setAttribute('aria-pressed', this.#paused ? 'true' : 'false');
  }

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const messages = this.refs.messages ?? [];
    const index = messages.findIndex(
      (message) => message === event.target || message.contains(/** @type {Node} */ (event.target))
    );
    if (index === -1) return;

    this.#held = true;
    this.#show(index);
  };

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    if ((this.refs.messages ?? []).some((message) => message === event.target)) this.#held = false;
  };
}

if (!customElements.get('orivon-announcement-component')) {
  customElements.define('orivon-announcement-component', OrivonAnnouncementComponent);
}
